import { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { emptyUsersGroup, UsersGroup } from "../interfaces/UsersGroup";
import { MainLayout } from "../layout/MainLayout";
import UsersGroupAdd from "./UsersGroupAdd";
import UsersGroupEdit from "./UsersGroupEdit";
import UsersGroupDelete from "./UsersGroupDelete";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../elements/LoadingSpinner";

export default function UsersGroups() {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const userId = localStorage.getItem('userId');
    
    const [usersGroups, setUsersGroups] = useState<UsersGroup[]>([]);
    const [selectedUsersGroup, setSelectedUsersGroup] = useState<UsersGroup>(emptyUsersGroup);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloadData, setReloadData] = useState(0);

    const columns: TableColumn<UsersGroup>[] = [
        {
            name: t("Id"),
            selector: (row: UsersGroup) => row._id,
            sortable: true,
            width: '200px',
            omit: true,
        },
        {
            name: t("Name"),
            selector: (row: UsersGroup) => row.name,
            sortable: true,
            grow: 2,
        },
        {
            name: t("Users"),
            selector: (row: UsersGroup) => {
                if (row.users.length === 0) return t("No users assigned");
                return row.users.length === 1 ? t("1 user") : row.users.length + ' ' + t("users");
            },
            sortable: false,
            width: '200px',
        },
        {
            name: t("Actions"),
            cell: (row: UsersGroup) => (
                <div className="actions">
                    <a
                        className="edit-link"
                        title={t("Edit group")}
                        href="#"
                        onClick={(event) => handleShowEditForm(event, row._id)}
                    ></a>
                    <a
                        className="delete-link"
                        title={t("Delete group")}
                        href="#"
                        onClick={(event) => handleShowDeleteForm(event, row._id)}
                    ></a>
                </div>
            ),
            width: '150px',
        },
    ];

    const ExpandedComponent = ({ data }: { data: UsersGroup }) => (
        <div style={{ padding: '15px' }}>
            <div><strong>{t("Id")}:</strong> {data._id}</div>
        </div>
    );

    useEffect(() => {
        setLoading(true);
        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/users-groups/' + userId, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                setUsersGroups(data);
                setLoading(false);
            });
    }, [reloadData]);

    const handleShowAddForm = () => {
        setShowAddForm(true);
    }

    const handleHideAddForm = () => {
        setShowAddForm(false);
    }

    const handleSumbitAddForm = (f: UsersGroup) => {
        setShowAddForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowDeleteForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedUsersGroup: UsersGroup = usersGroups.find((o: UsersGroup) => o._id == id) ?? emptyUsersGroup;
        if (newSelectedUsersGroup._id != '') {
            setSelectedUsersGroup(newSelectedUsersGroup);
            setShowDeleteForm(true);
        } else {
            alert(t("No organization selected."));
        }
    }
    
    const handleHideDeleteForm = () => {
        setShowDeleteForm(false);
    }

    const handleSumbitDeleteForm = (id: string) => {
        setShowDeleteForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowEditForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedUsersGroup: UsersGroup = usersGroups.find((o: UsersGroup) => o._id == id) ?? emptyUsersGroup;
        if (newSelectedUsersGroup._id != '') {
            setSelectedUsersGroup(newSelectedUsersGroup);
            setShowEditForm(true);
        } else {
            alert(t("No organization selected."));
        }
    }
    
    const handleHideEditForm = () => {
        setShowEditForm(false);
    }

    const handleSumbitEditForm = (usersGroup: UsersGroup) => {
        setShowEditForm(false);
        setReloadData(prev => prev + 1);
    }

    return (
        <MainLayout>
            <div className="container-fluid">
                <div className="row">
                    <div className="usersgroup-container">
                        <div className="page-header">
                            <h1>{ t("Users groups") }</h1>
                            <div className="page-actions">
                                <a className="action-icon" onClick={ handleShowAddForm } title={ t("New group") }>
                                    <i className="bi bi-plus-circle"></i>
                                </a>
                            </div>
                        </div>
                        <div className="usersgroup-table-container">
                            <DataTable
                                columns={columns}
                                data={usersGroups}
                                progressPending={loading}
                                progressComponent={<LoadingSpinner />}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                highlightOnHover
                                striped
                                responsive
                                noDataComponent={t("No groups found")}
                                expandableRows
                                expandableRowsComponent={ExpandedComponent}
                            />
                        </div>
                    </div>
                </div>
            </div>
            { showAddForm &&
                <UsersGroupAdd submitCallback={ handleSumbitAddForm } closeCallback={ handleHideAddForm } />
            }
            { showDeleteForm &&
                <UsersGroupDelete usersGroup={ selectedUsersGroup } submitCallback={ handleSumbitDeleteForm } closeCallback={ handleHideDeleteForm } />
            }
            { showEditForm &&
                <UsersGroupEdit usersGroup={ selectedUsersGroup } submitCallback={ handleSumbitEditForm } closeCallback={ handleHideEditForm } />
            }
        </MainLayout>);
}